import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqData = [
    {
      question: "¿Es legal pagar por adelantado el alquiler por varios años?",
      answer: "Sí, es completamente legal. Según el artículo 17 de la Ley de Arrendamientos Urbanos (LAU), es posible pactar el pago anticipado del alquiler. Todos nuestros contratos son supervisados por abogados especializados en derecho inmobiliario y cumplen con la normativa vigente."
    },
    {
      question: "¿Qué diferencia hay entre los planes de 2, 3 y 5 años?",
      answer: "Ofrecemos tres planes flexibles: Plan Estándar (2 años) con 20% de descuento, Plan Ventaja (3 años) con 25% de descuento, y Plan Máximo (5 años) con 30% de descuento. Cuanto mayor sea el compromiso temporal, mayor es el descuento que obtienes."
    },
    {
      question: "¿Qué pasa si el propietario no cumple con sus obligaciones?",
      answer: "Tenemos un seguro de caución que protege al inquilino durante todo el período contratado. Si el propietario incumple (por ejemplo, no entrega la vivienda o hay vicios ocultos), el inquilino está cubierto financieramente. Además, incluimos cláusulas específicas de penalización por incumplimiento del propietario."
    },
    {
      question: "¿Y si el inquilino quiere abandonar la vivienda antes del período acordado?",
      answer: "El contrato incluye cláusulas de salida anticipada en casos justificados (traslado laboral, motivos familiares graves, etc.). En estos casos, se realiza una liquidación proporcional. Para otros supuestos, se aplican las penalizaciones establecidas en el contrato, siempre dentro del marco legal."
    },
    {
      question: "¿Qué garantías existen para ambas partes en todos los planes?",
      answer: "Ofrecemos múltiples garantías: contratos supervisados legalmente, seguro de caución, gestión de fianzas mediante depósito legal, mediación en caso de conflictos, y un proceso de verificación exhaustiva de ambas partes antes de la firma. Las garantías se mantienen durante todo el período contratado."
    },
    {
      question: "¿Qué documentación se necesita para acceder a este servicio?",
      answer: "Para propietarios: escrituras de propiedad, cédula de habitabilidad, última factura de IBI, y DNI. Para inquilinos: justificantes de ingresos (según el plan elegido), referencias bancarias, DNI, y aval bancario o garantía adicional proporcional al período del contrato."
    },
    {
      question: "¿Cómo se realiza la firma del contrato y cuándo?",
      answer: "La firma siempre es presencial, en notaría o en nuestras oficinas con presencia legal. Se coordina una cita donde ambas partes revisan el contrato final, se efectúa el pago del total acordado según el plan elegido, y se formalizan todas las garantías. El proceso completo toma entre 1-2 semanas desde el match inicial."
    },
    {
      question: "¿Cuándo y cómo se paga vuestra comisión?",
      answer: "Nuestra comisión se paga únicamente cuando hay acuerdo exitoso y se firma el contrato. No cobramos por consultas, búsquedas, o procesos que no concluyan en firma. La comisión es un porcentaje fijo que se descuenta del primer pago, transparente y sin costes ocultos, independientemente del plan elegido."
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-6">
            <HelpCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Preguntas frecuentes
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Resolvemos las dudas más comunes sobre nuestro modelo de alquiler prepago
          </p>
        </div>

        <div className="space-y-4">
          {faqData.map((item, index) => {
            const isOpen = openItems.has(index);
            
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-xl"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-green-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">
                    {item.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {isOpen ? (
                      <ChevronUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>
                
                {isOpen && (
                  <div 
                    id={`faq-answer-${index}`}
                    className="px-6 pb-6 animate-fade-in"
                  >
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              ¿Tienes alguna otra pregunta?
            </h3>
            <p className="text-gray-600 mb-6">
              Nuestro equipo está disponible para resolver cualquier duda específica sobre tu caso
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+34684059141"
                className="inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full transition-colors duration-200"
              >
                Llamar ahora: +34 684 059 141
              </a>
              <a 
                href="mailto:info@contactoadelantado.com"
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-full transition-colors duration-200"
              >
                Enviar email
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;